import { useRef, useCallback, useEffect, useState } from 'react';

interface UseGridNavigationOptions {
  itemCount: number;
  columns: number;
  enabled?: boolean;
  onSelect?: (index: number) => void;
}

export function useGridNavigation({
  itemCount,
  columns,
  enabled = true,
  onSelect,
}: UseGridNavigationOptions) {
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());

  const registerItem = useCallback((index: number, element: HTMLElement | null) => {
    if (element) {
      itemRefs.current.set(index, element);
    } else {
      itemRefs.current.delete(index);
    }
  }, []);

  const focusItem = useCallback((index: number) => {
    const element = itemRefs.current.get(index);
    if (element) {
      const focusableElement = element.querySelector<HTMLElement>('a, button');
      if (focusableElement) {
        focusableElement.focus();
        setFocusedIndex(index);
      }
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled || focusedIndex === -1) return;

      const rows = Math.ceil(itemCount / columns);
      const currentRow = Math.floor(focusedIndex / columns);
      const currentCol = focusedIndex % columns;

      let newIndex = focusedIndex;

      switch (e.key) {
        case 'ArrowRight':
          if (focusedIndex < itemCount - 1) {
            newIndex = focusedIndex + 1;
          }
          break;
        case 'ArrowLeft':
          if (focusedIndex > 0) {
            newIndex = focusedIndex - 1;
          }
          break;
        case 'ArrowDown':
          if (currentRow < rows - 1) {
            const targetIndex = (currentRow + 1) * columns + currentCol;
            newIndex = Math.min(targetIndex, itemCount - 1);
          }
          break;
        case 'ArrowUp':
          if (currentRow > 0) {
            newIndex = (currentRow - 1) * columns + currentCol;
          }
          break;
        case 'Home':
          if (e.ctrlKey) {
            newIndex = 0;
          } else {
            newIndex = currentRow * columns;
          }
          break;
        case 'End':
          if (e.ctrlKey) {
            newIndex = itemCount - 1;
          } else {
            newIndex = Math.min((currentRow + 1) * columns - 1, itemCount - 1);
          }
          break;
        case 'Enter':
        case ' ':
          if (onSelect) {
            e.preventDefault();
            onSelect(focusedIndex);
          }
          return;
        default:
          return;
      }

      if (newIndex !== focusedIndex) {
        e.preventDefault();
        focusItem(newIndex);
      }
    },
    [enabled, focusedIndex, itemCount, columns, onSelect, focusItem]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);

  const handleFocus = useCallback((index: number) => {
    setFocusedIndex(index);
  }, []);

  const getItemProps = useCallback(
    (index: number) => ({
      ref: (el: HTMLElement | null) => registerItem(index, el),
      tabIndex: focusedIndex === -1 && index === 0 ? 0 : focusedIndex === index ? 0 : -1,
      onFocus: () => handleFocus(index),
      'data-grid-index': index,
    }),
    [focusedIndex, registerItem, handleFocus]
  );

  return {
    containerRef,
    focusedIndex,
    getItemProps,
    focusItem,
    setFocusedIndex,
  };
}
